import { darken, lighten } from 'polished';
import styled, { css } from 'styled-components';

import type { PrivacyCenterTheme } from '@main/theme';
import { switchCase } from '@main/utils';

import type { ButtonType } from './types';

/**
 * Reset a <button> to have no style so that you're building from a blank slate
 */
export const buttonReset = css`
  background: none;
  border: 0;
  color: inherit;
  font: inherit;
  line-height: normal;
  overflow: visible;
  padding: 0;
  outline: none;
  user-select: none; /* for button */
`;

/**
 * The core styling of a button, can also be applied to links (A) to appear as if a button
 */
export const coreButtonStyles = css`
  padding: 1em 1.8em;
  width: auto;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s;
`;

/**
 * Props for a button
 */
export interface ButtonProps {
  /** Styles */
  styles?: string;
  /** The type of button -- determines color. Note: prop `type` conflicts with HTML button prop */
  buttonType?: ButtonType;
  /** Theme -- only added for helper function use */
  theme: PrivacyCenterTheme;
  /** Flag for disabled button */
  disabled?: boolean;
}

// Get the background color based on type and theme
const getFillColor = ({ theme, buttonType }: ButtonProps): string =>
  switchCase<string, [], ButtonType>({
    primary: theme.colors.primary,
    secondary: lighten(0.65, theme.colors.secondary),
    transcend: lighten(0.37, theme.colors.transcend),
  })(theme.colors.primary)(buttonType || 'primary');

// Get the text color based on type and theme
const getTextColor = ({ theme, buttonType }: ButtonProps): string =>
  switchCase<string, [], ButtonType>({
    primary: theme.colors.textOnPrimary,
    secondary: theme.colors.secondary,
    transcend: theme.colors.transcend,
  })(theme.colors.textOnPrimary)(buttonType || 'primary');

// Get the active on-hold color based on type and theme
const getActiveColor = ({ theme, buttonType }: ButtonProps): string =>
  switchCase<string, [], ButtonType>({
    primary: theme.colors.white,
    secondary: theme.colors.secondary,
    transcend: theme.colors.transcend,
  })(theme.colors.textOnPrimary)(buttonType || 'primary');

// Get the active on-hold background color based on type and theme
const getActiveBgColor = switchCase<string, [PrivacyCenterTheme], ButtonType>({
  primary: (theme) => darken(0.15, theme.colors.primary),
  secondary: (theme) => darken(0.05, lighten(0.65, theme.colors.secondary)),
  transcend: (theme) => darken(0.04, lighten(0.37, theme.colors.transcend)), // TODO direct calculation here
})((theme) => theme.colors.textOnPrimary);

// Styled when active
const activeStyles = css`
  outline: none;
  background-color: ${({ theme, buttonType }: ButtonProps): string =>
    getActiveBgColor(buttonType || 'primary', theme)};
  color: ${getActiveColor};
`;

/**
 * Button
 */
export const StyledButton = styled.button<ButtonProps>`
  ${buttonReset}
  ${coreButtonStyles}
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.8 : 1)};
  font-family: ${({ theme, buttonType }) =>
    buttonType === 'transcend'
      ? 'neue-haas-grotesk-display, sans-serif'
      : theme.textStyles.fontFamilyHeader.name};
  background-color: ${getFillColor};
  color: ${getTextColor};

  &:hover,
  &:focus {
    ${({ buttonType, disabled }) =>
      disabled
        ? ''
        : css`
            background-color: ${({ theme }) =>
              buttonType === 'primary'
                ? darken(0.1, getFillColor({ theme, buttonType }))
                : buttonType === 'transcend'
                  ? darken(0.02, getFillColor({ theme, buttonType }))
                  : darken(0.05, getFillColor({ theme, buttonType }))};
          `}
  }

  &:focus {
    box-shadow:
      ${({ theme }) => theme.colors.lightPrimary} 0 0 1px 3px,
      ${({ theme }) => theme.colors.lightPrimary} 0 0 0 1px,
      ${({ theme }) => theme.colors.lightPrimary} 0 2px 6px 0,
      ${({ theme }) => theme.colors.lightPrimary} 0 1px 3.5px 0;
  }

  &:active {
    ${activeStyles}
  }

  ${({ theme, buttonType }) =>
    buttonType === 'secondary'
      ? theme.componentStyles.secondaryButton
      : buttonType === 'primary'
        ? theme.componentStyles.primaryButton
        : ''};
`;
